import { darken, mix } from 'polished';
import React, { useCallback, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { promptName, useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Check } from '../../../ui/Check';
import { Tip } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { useEditorSessionObserver } from '../../canvas/EditorSession';
import { FixMenuWrap } from '../../canvas/participants/FixMenuWrap';
import { ParticipantData } from '../../canvas/participants/Participant';
import { ParticipantStripInfo } from '../../canvas/participants/ParticipantStripInfo';
import { useParticipantStripObserver } from '../../canvas/participants/ParticipantStripWidget';
import { PopperInner, PopperMount } from '../../canvas/ui/PopperElements';
import { InlineWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { Colors, Float, Prompt, remInPx } from './../../../ui';
import { readyCheckRespond } from './ReadyCheckCommands';
import { LiveReadyCheck } from './ReadyCheckTypes';

const XScrollbars = Scrollbars as any;

const ItemFrame = styled.div`
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    height: 2.75rem;
    margin-bottom: 0.25rem;
`;

const InputFrame = styled.div<{ ready?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .375rem;
    padding: 0 .5rem 0 .375rem;
    height: 2.5rem;
    background: ${p => p.ready ? '#fff' : '#fff'}; 
    border: solid .125rem ${p => p.ready ? Colors.positive : Colors.border};
    border-radius: 0.25rem;
    color: ${p => p.ready ? Colors.positive : Colors.textLight};
    font-size: 1.125rem;
    font-weight: 450;
    cursor: pointer;
    user-select: none;

    ${p => !p.ready && css`
    &:hover {
        border-color: ${darken(0.05, Colors.border)};
    }
    &:active {
        border-color: ${darken(0.10, Colors.border)};
    }
    `}
`;

const SummaryFrame = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 2.5rem;
    border-radius: 0.25rem;
    color: white;
    font-size: 1.125rem;
    font-weight: 450;
    cursor: pointer;
    user-select: none;

    &:before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 0.25rem;
        pointer-events: none;
    }

    &:hover {
        &:before { background: rgba(0, 0, 0, .05) };
    }
    &:active {
        &:before { background: rgba(0, 0, 0, .10) };
    }
`;

const ReadyToggle = React.memo<{ ready: boolean }>(({ ready }) => {
    const dispatch = useResolve(CommandDispatcher);
    const onToggle = useCallback(() => void (dispatch(readyCheckRespond, [!ready])), [ready]);
    return <InputFrame ready={ready} onClick={onToggle}>
        <Check value={ready} />{"I'm ready"}
    </InputFrame>;
});

const PendingItem = styled.div`
    display: flex;
    align-items: center;    
    margin-top: 0.25rem;

    span {
        margin-left: 0.75rem;
    }
`;

const SummaryItem: LFC<{ ready: ParticipantData[], online: ParticipantData[] }> = (({ ready, online }) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'left-start',
    });

    const pn = promptName();
    const [open, setOpen] = useState(false);
    const onOpen = useCallback(() => setOpen(!open), [open, setOpen]);
    const onClose = useCallback(() => setOpen(false), [setOpen]);

    const background = mix(ready.length / online.length, Colors.positive, Colors.negative);
    const pending = online.filter(x => !ready.includes(x));

    return <>
        <SummaryFrame ref={setRefElmt} style={{ background }} onClick={onOpen}>
            {ready.length} / {online.length}
        </SummaryFrame>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter onAction={onClose}>
                <PopperInner direction="left">
                    <FixMenuWrap>
                        <Float shadow="lg">
                            <Flexer padding=".75 2.5 .75 .75">
                                {pending.length === 0 ? <>
                                    <Title flush>Everyone is ready!</Title>
                                </> : <>
                                    <XScrollbars autoHeight autoHeightMax={remInPx(4 * 8)}>
                                        <Title>We&apos;re still waiting for:</Title>
                                        {pending.map(x =>
                                            <PendingItem key={x.id}>{x.name}</PendingItem>
                                        )}
                                    </XScrollbars>
                                </>}
                            </Flexer>
                        </Float>
                    </FixMenuWrap>
                </PopperInner>
            </Prompt>
        </PopperMount>
        }
    </>;

});

export const ReadyCheckWidget: LFC<{ widget: InlineWidget }> = ({ widget }) => {

    const me = useResolve(ProfileModel);
    const entries = useObserver(widget.store as LiveReadyCheck);
    const [all, visible] = useParticipantStripObserver();
    const strip = useEditorSessionObserver(ParticipantStripInfo);
    const online = useMemo(() => all.filter(x => x.presence !== 'offline'), [all]);
    const ready = useMemo(() => online.filter(x => !!entries[x.id]), [online, entries]);

    // If strip not visible, just show me
    const list = useMemo(() => strip.enabled ? visible : visible.filter(x => x.id === me.id), [strip, visible, me]);

    return <Flexer flex="column">
        {list.map(x => <ItemFrame key={x.id}>
            {x.id === me.id ? <>
                <ReadyToggle ready={!!entries[me.id]} />
            </> : <>
                <Tip text={`${x.name} is ${!entries[x.id] ? ' not' : ''} ready`} position="left">
                    <Check key={x.id + 'check'} value={!!entries[x.id]} />
                </Tip>
            </>
            }
        </ItemFrame>)}
        <ItemFrame>
            <SummaryItem key="summary" {...{ ready, online }} />
        </ItemFrame>
    </Flexer>;

    {/* return <Box width="11" padding=".25">
        <ActionLayout mode="grid:4" padding={1}>
            {items.map(([id, label, icon, kind]) => (
                <ActionItem key={id} title={label} pressable selected={value === kind} onClick={this.onSelect(kind)}>
                    <Image width={24} height={24} src={icon} />
                </ActionItem>
            ))}
        </ActionLayout>
    </Box>; */}
};
